import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createAssignment, createOrganization, createReport, gradeReport,
  listAssignments, listOrganizations, listReports, myPractice,
  PRACTICE_KINDS, returnReport, submitReport, updateOrganization,
} from "service/PracticeService";
import { listStudents } from "service/StudentsService";
import { listEmployees } from "service/HRService";
import { useSelector } from "react-redux";
import Notification from "utils/Notifications";
import s from "./PracticePage.module.scss";

export default function PracticePage() {
  const { t } = useTranslation('common');
  const auth = useSelector((st) => st.auth);
  const codes = (auth?.roles || []).map((r) => (typeof r === "string" ? r : r?.code));
  const isStaff = codes.some((c) => ["methodist", "dean", "head_of_department", "rectorate", "sysadmin"].includes(c));
  const isStudent = codes.includes("student");

  const [tab, setTab] = useState(isStaff ? "assignments" : "my");
  const [orgs, setOrgs] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [reports, setReports] = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editingAssign, setEditingAssign] = useState(null);
  const [grading, setGrading] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [notify, setNotify] = useState({ isOpen: false });

  const reload = () => {
    listOrganizations().then((r) => setOrgs(r.data?.results || r.data || []));
    if (isStaff) {
      listAssignments().then((r) => setAssignments(r.data?.results || r.data || []));
      listReports().then((r) => setReports(r.data?.results || r.data || []));
      listStudents({ page_size: 200 }).then((r) => setStudents(r.data?.results || r.data || []));
      listEmployees({ status: "active", page_size: 200 }).then((r) => setTeachers(r.data?.results || r.data || []));
    }
    if (isStudent) myPractice().then((r) => setMyAssignments(r.data?.results || r.data || []));
  };
  useEffect(() => { reload();  }, []);

  const saveOrg = async () => {
    try {
      if (editingOrg.id) await updateOrganization(editingOrg.id, editingOrg);
      else await createOrganization(editingOrg);
      setEditingOrg(null); reload();
    } catch { setNotify({ isOpen: true, message: t('common.error'), type: "error" }); }
  };
  const saveAssign = async () => {
    try {
      await createAssignment(editingAssign);
      setEditingAssign(null); reload();
    } catch { setNotify({ isOpen: true, message: t('common.error'), type: "error" }); }
  };

  return (
    <div className={s.page}>
      <div className={s.toolbar}>
        <h2 className={s.title}>{t('practice.title_page')}</h2>
        <div className={s.tabs}>
          {isStaff && <button type="button" role="tab" aria-selected={tab === "assignments"} className={`${s.tab} ${tab === "assignments" ? s.active : ""}`} onClick={() => setTab("assignments")}>{t('practice.tab_assignments')}</button>}
          {isStaff && <button type="button" role="tab" aria-selected={tab === "reports"} className={`${s.tab} ${tab === "reports" ? s.active : ""}`} onClick={() => setTab("reports")}>{t('practice.tab_reports')}</button>}
          {isStaff && <button type="button" role="tab" aria-selected={tab === "orgs"} className={`${s.tab} ${tab === "orgs" ? s.active : ""}`} onClick={() => setTab("orgs")}>{t('practice.tab_orgs')}</button>}
          {isStudent && <button type="button" role="tab" aria-selected={tab === "my"} className={`${s.tab} ${tab === "my" ? s.active : ""}`} onClick={() => setTab("my")}>{t('practice.tab_my')}</button>}
        </div>
      </div>

      {tab === "orgs" && (
        <>
          <div className={s.toolbar}>
            <button className="unv-btn unv-btn--primary"
                    onClick={() => setEditingOrg({ is_active: true })}>{t('practice.add_org')}</button>
          </div>
          <table className={s.table}>
            <thead><tr><th>{t('practice.col_name')}</th><th>{t('practice.col_address')}</th><th>{t('practice.col_contact')}</th><th>{t('practice.col_phone')}</th><th></th></tr></thead>
            <tbody>
              {orgs.map((o) => (
                <tr key={o.id}>
                  <td>{o.name}</td><td>{o.address || "—"}</td>
                  <td>{o.contact_person || "—"}</td><td>{o.contact_phone || "—"}</td>
                  <td><button className={s.smallBtn} onClick={() => setEditingOrg(o)}>{t('practice.edit_short')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {tab === "assignments" && isStaff && (
        <>
          <div className={s.toolbar}>
            <button className="unv-btn unv-btn--primary"
                    onClick={() => setEditingAssign({ kind: "production", status: "planned" })}>
              {t('practice.add_assignment')}
            </button>
          </div>
          <table className={s.table}>
            <thead><tr><th>{t('practice.col_student')}</th><th>{t('practice.col_org')}</th><th>{t('practice.col_kind')}</th><th>{t('practice.col_period')}</th><th>{t('practice.col_status')}</th></tr></thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id}>
                  <td>{a.student_label}</td><td>{a.organization_name}</td>
                  <td>{a.kind_display}</td>
                  <td>{a.period_from} → {a.period_to}</td>
                  <td>{a.status_display}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {tab === "reports" && isStaff && (
        <table className={s.table}>
          <thead><tr><th>{t('practice.col_student_org')}</th><th>{t('practice.col_submitted')}</th><th>{t('practice.col_grade')}</th><th>{t('practice.col_status')}</th><th></th></tr></thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.id}>
                <td>{r.assignment_label}</td>
                <td>{r.submitted_at ? r.submitted_at.slice(0, 10) : "—"}</td>
                <td>{r.grade || "—"} {r.grade_letter || ""}</td>
                <td>{r.status_display}</td>
                <td>
                  {r.file && <a className={s.smallBtn} href={r.file} target="_blank" rel="noreferrer">{t('practice.file_btn')}</a>}
                  {r.status === "submitted" && (
                    <>
                      <button className={s.smallBtn} onClick={() => setGrading({ id: r.id, grade: "", grade_letter: "", teacher_comment: "" })}>{t('practice.grade_btn')}</button>
                      <button className={s.smallBtn} onClick={async () => {
                        const c = prompt(t('practice.comment_prompt')); if (!c) return;
                        await returnReport(r.id, { teacher_comment: c });
                        reload();
                      }}>{t('practice.return_btn')}</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === "my" && isStudent && (
        <table className={s.table}>
          <thead><tr><th>{t('practice.col_org')}</th><th>{t('practice.col_kind')}</th><th>{t('practice.col_period')}</th><th>{t('practice.col_status')}</th><th>{t('practice.col_report')}</th></tr></thead>
          <tbody>
            {myAssignments.map((a) => (
              <tr key={a.id}>
                <td>{a.organization_name}</td><td>{a.kind_display}</td>
                <td>{a.period_from} → {a.period_to}</td><td>{a.status_display}</td>
                <td>
                  <button className={s.smallBtn} onClick={() => setSubmitting({ assignment: a.id })}>{t('practice.upload_report')}</button>
                </td>
              </tr>
            ))}
            {myAssignments.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", color: "#9aa4bc" }}>{t('practice.no_assignments')}</td></tr>}
          </tbody>
        </table>
      )}

      {editingOrg && (
        <div className={s.overlay} onClick={() => setEditingOrg(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{editingOrg.id ? t('practice.modal_edit_org') : t('practice.modal_new_org')}</h3>
            <div className={s.field}><label>{t('practice.field_name')}</label>
              <input className="unv-input" value={editingOrg.name || ""}
                     onChange={(e) => setEditingOrg({ ...editingOrg, name: e.target.value })} /></div>
            <div className={s.field}><label>{t('practice.field_address')}</label>
              <input className="unv-input" value={editingOrg.address || ""}
                     onChange={(e) => setEditingOrg({ ...editingOrg, address: e.target.value })} /></div>
            <div className={s.field}><label>{t('practice.field_contact_person')}</label>
              <input className="unv-input" value={editingOrg.contact_person || ""}
                     onChange={(e) => setEditingOrg({ ...editingOrg, contact_person: e.target.value })} /></div>
            <div className={s.field}><label>{t('practice.field_phone_email')}</label>
              <input className="unv-input" value={editingOrg.contact_phone || ""}
                     onChange={(e) => setEditingOrg({ ...editingOrg, contact_phone: e.target.value })} style={{ marginBottom: 6 }} />
              <input className="unv-input" value={editingOrg.contact_email || ""}
                     onChange={(e) => setEditingOrg({ ...editingOrg, contact_email: e.target.value })} /></div>
            <div className={s.actions}>
              <button className="unv-btn" onClick={() => setEditingOrg(null)}>{t('common.cancel')}</button>
              <button className="unv-btn unv-btn--primary" onClick={saveOrg}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      {editingAssign && (
        <div className={s.overlay} onClick={() => setEditingAssign(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('practice.modal_new_assignment')}</h3>
            <div className={s.field}><label>{t('practice.field_student')}</label>
              <select className="unv-input" value={editingAssign.student || ""}
                      onChange={(e) => setEditingAssign({ ...editingAssign, student: Number(e.target.value) })}>
                <option value="">—</option>
                {students.map((st) => (<option key={st.id} value={st.id}>{st.full_name || `${st.surname} ${st.first_name}`}</option>))}
              </select></div>
            <div className={s.field}><label>{t('practice.field_org')}</label>
              <select className="unv-input" value={editingAssign.organization || ""}
                      onChange={(e) => setEditingAssign({ ...editingAssign, organization: Number(e.target.value) })}>
                <option value="">—</option>
                {orgs.map((o) => (<option key={o.id} value={o.id}>{o.name}</option>))}
              </select></div>
            <div className={s.field}><label>{t('practice.field_kind')}</label>
              <select className="unv-input" value={editingAssign.kind}
                      onChange={(e) => setEditingAssign({ ...editingAssign, kind: e.target.value })}>
                {Object.entries(PRACTICE_KINDS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
              </select></div>
            <div className={s.field}><label>{t('practice.field_period_from_to')}</label>
              <input type="date" className="unv-input" value={editingAssign.period_from || ""}
                     onChange={(e) => setEditingAssign({ ...editingAssign, period_from: e.target.value })} style={{ marginBottom: 6 }} />
              <input type="date" className="unv-input" value={editingAssign.period_to || ""}
                     onChange={(e) => setEditingAssign({ ...editingAssign, period_to: e.target.value })} /></div>
            <div className={s.field}><label>{t('practice.field_supervisor_internal')}</label>
              <select className="unv-input" value={editingAssign.supervisor_internal || ""}
                      onChange={(e) => setEditingAssign({ ...editingAssign, supervisor_internal: Number(e.target.value) })}>
                <option value="">—</option>
                {teachers.map((tt) => (<option key={tt.id} value={tt.id}>{tt.full_name || `${tt.surname} ${tt.first_name}`}</option>))}
              </select></div>
            <div className={s.field}><label>{t('practice.field_order_no')}</label>
              <input className="unv-input" value={editingAssign.order_no || ""}
                     onChange={(e) => setEditingAssign({ ...editingAssign, order_no: e.target.value })} /></div>
            <div className={s.actions}>
              <button className="unv-btn" onClick={() => setEditingAssign(null)}>{t('common.cancel')}</button>
              <button className="unv-btn unv-btn--primary" onClick={saveAssign}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      {submitting && (
        <div className={s.overlay} onClick={() => setSubmitting(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('practice.upload_report_title')}</h3>
            <div className={s.field}><label>{t('practice.field_description')}</label>
              <textarea className="unv-input" rows={3} value={submitting.text || ""}
                        onChange={(e) => setSubmitting({ ...submitting, text: e.target.value })} /></div>
            <div className={s.field}><label>{t('practice.field_report_file')}</label>
              <input type="file" onChange={(e) => setSubmitting({ ...submitting, file: e.target.files[0] })} /></div>
            <div className={s.field}><label>{t('practice.field_diary')}</label>
              <input type="file" onChange={(e) => setSubmitting({ ...submitting, diary_file: e.target.files[0] })} /></div>
            <div className={s.field}><label>{t('practice.field_feedback')}</label>
              <input type="file" onChange={(e) => setSubmitting({ ...submitting, feedback_file: e.target.files[0] })} /></div>
            <div className={s.actions}>
              <button className="unv-btn" onClick={() => setSubmitting(null)}>{t('common.cancel')}</button>
              <button className="unv-btn unv-btn--primary" onClick={async () => {
                try {
                  const r = await createReport(submitting);
                  await submitReport(r.data.id);
                  setNotify({ isOpen: true, message: t('practice.report_submitted'), type: "success" });
                  setSubmitting(null);
                  reload();
                } catch { setNotify({ isOpen: true, message: t('common.error'), type: "error" }); }
              }}>{t('practice.submit_btn')}</button>
            </div>
          </div>
        </div>
      )}

      {grading && (
        <div className={s.overlay} onClick={() => setGrading(null)}>
          <div className={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3>{t('practice.grade_report_title')}</h3>
            <div className={s.field}><label>{t('practice.field_grade_0_100')}</label>
              <input type="number" className="unv-input" value={grading.grade}
                     onChange={(e) => setGrading({ ...grading, grade: e.target.value })} /></div>
            <div className={s.field}><label>{t('practice.field_grade_letter')}</label>
              <input className="unv-input" value={grading.grade_letter}
                     onChange={(e) => setGrading({ ...grading, grade_letter: e.target.value })} /></div>
            <div className={s.field}><label>{t('practice.field_comment')}</label>
              <textarea className="unv-input" rows={3} value={grading.teacher_comment}
                        onChange={(e) => setGrading({ ...grading, teacher_comment: e.target.value })} /></div>
            <div className={s.actions}>
              <button className="unv-btn" onClick={() => setGrading(null)}>{t('common.cancel')}</button>
              <button className="unv-btn unv-btn--primary" onClick={async () => {
                try {
                  await gradeReport(grading.id, grading);
                  setGrading(null); reload();
                } catch { setNotify({ isOpen: true, message: t('common.error'), type: "error" }); }
              }}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
}
